import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { 
  getConnections, 
  getAuthorizationUrl, 
  disconnectConnection as disconnectConnectionApi 
} from '../services/oauthApi';
import { openOAuthPopup, isUserCancelledError, getOAuthErrorMessage } from '../utils/oauthPopup';
import { useAuth } from './AuthContext';

const OAuthContext = createContext();

export const useOAuth = () => {
  const context = useContext(OAuthContext);
  if (!context) {
    throw new Error('useOAuth must be used within an OAuthProvider');
  }
  return context;
};

export const OAuthProvider = ({ children }) => {
  const { getUserIdDiscover, getAccessToken, loading: authLoading, user } = useAuth();
  const [connections, setConnections] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all connections
  const fetchConnections = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const userId = getUserIdDiscover();
      const data = await getConnections(userId, getAccessToken());
      setConnections(data);
    } catch (err) {
      console.error('Failed to fetch connections:', err);
      setError(err.message);
      setConnections([]);
    } finally {
      setIsLoading(false);
    }
  }, [getUserIdDiscover, getAccessToken]);

  // Not on mount — on auth. This provider mounts inside AuthProvider but before
  // Keycloak has resolved a session, so firing immediately sent /connections with
  // a null bearer token: a guaranteed 401 whose catch block clears `connections`
  // and sets an error. The authenticated fetch that follows once auth resolves
  // then races that failure, and if the 401 lands second it wipes valid
  // connections. Waiting costs nothing — there is no useful unauthenticated
  // answer from this endpoint.
  //
  // `user?.id` rather than `user`: the object identity changes on every silent
  // token renewal, and refetching the connection list on each renewal is churn.
  useEffect(() => {
    if (authLoading || !user) return;
    fetchConnections();
    // fetchConnections is excluded deliberately — it depends on getAccessToken /
    // getUserIdDiscover, which get new identities on every AuthContext render, so
    // including it would refetch on every render rather than on every login.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.id]);

  // Initiate OAuth flow
  const initiateOAuth = useCallback(async (provider) => {
    try {
      setIsConnecting(true);
      setError(null);
      
      const userId = getUserIdDiscover();
      const authUrl = await getAuthorizationUrl(provider, userId, getAccessToken());

      const result = await openOAuthPopup(authUrl);

      // Refresh connections after successful OAuth
      await fetchConnections();

      return { success: true, ...result };
    } catch (err) {
      if (isUserCancelledError(err)) {
        return { success: false, cancelled: true, message: 'Authorization was cancelled' };
      }

      const errorMessage = getOAuthErrorMessage(err);
      setError(errorMessage);
      return { success: false, cancelled: false, message: errorMessage };
    } finally {
      setIsConnecting(false);
    }
  }, [fetchConnections, getUserIdDiscover, getAccessToken]);

  // Disconnect a connection by provider name
  const disconnectProvider = useCallback(async (provider) => {
    try {
      setError(null);
      const userId = getUserIdDiscover();
      await disconnectConnectionApi(provider, userId, getAccessToken());

      // Refresh connections after disconnect
      await fetchConnections();

      return { success: true };
    } catch (err) {
      const errorMessage = err.message || 'Failed to disconnect';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    }
  }, [fetchConnections, getUserIdDiscover, getAccessToken]);

  // Refresh connections (manual)
  const refreshConnections = useCallback(() => {
    return fetchConnections();
  }, [fetchConnections]);

  // Check if a provider is connected
  const isProviderConnected = useCallback((provider) => {
    return connections.some(conn => conn.provider?.toLowerCase() === provider?.toLowerCase() && conn.status === 'active');
  }, [connections]);

  // Get connection for a specific provider
  const getProviderConnection = useCallback((provider) => {
    return connections.find(conn => conn.provider?.toLowerCase() === provider?.toLowerCase());
  }, [connections]);

  const value = {
    connections,
    isLoading,
    isConnecting,
    error,
    initiateOAuth,
    disconnectProvider,
    refreshConnections,
    isProviderConnected,
    getProviderConnection,
  };

  return (
    <OAuthContext.Provider value={value}>
      {children}
    </OAuthContext.Provider>
  );
};

OAuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};